import { useEffect, useRef, useState } from 'react';
import useEmblaCarousel from '../vendor/embla-carousel-react';
import { useReducedMotion } from 'framer-motion';
import { scrollBy, scrollProgress as getScrollProgress } from '../lib/embla';
import { useWindowSize } from './useWindowSize';
import type { EmblaCarouselType } from '../vendor/embla-carousel';

interface Options {
  itemCount: number;
  /** Optional spacing override for desktop */
  spacing?: number;
  /** Optional parabola override for desktop */
  parabola?: number;
  /** Optional spacing override for mobile viewports */
  mobileSpacing?: number;
  /** Optional parabola override for mobile viewports */
  mobileParabola?: number;
  /** Force mobile layout regardless of viewport width */
  forceMobileLayout?: boolean;
  /** Index to restore after reinitialization */
  initialIndex?: number;
  /** Callback fired after Embla is reinitialized */
  onReInit?: (index: number) => void;
  /** Callback fired once Embla is fully initialized */
  onInit?: (api: EmblaCarouselType) => void;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const LOW_SPEED_THRESHOLD = 0.01;

/**
 * Progress difference threshold for centering calculations.
 * Tune this value to adjust how precise the centering should be.
 */
const CENTERING_THRESHOLD = 0.002;

export const useManualEmblaDrag = ({
  itemCount,
  spacing,
  parabola,
  mobileSpacing,
  mobileParabola,
  forceMobileLayout,
  initialIndex,
  onReInit,
  onInit,
}: Options) => {
  const initialIndexRef = useRef(initialIndex);
  const { width } = useWindowSize();
  const isMobile = forceMobileLayout || width <= 768;
  const spacingRatio = 440 / 1920;
  const parabolaRatio = 100 / 1920;
  const spacingValue = isMobile
    ? mobileSpacing ?? width * spacingRatio
    : spacing ?? width * spacingRatio;
  const parabolaValue = isMobile
    ? mobileParabola ?? width * parabolaRatio
    : parabola ?? width * parabolaRatio;
  const initialCarouselRadius =
    parabolaValue > 0 ? (spacingValue * spacingValue) / (2 * parabolaValue) : 0;
  const [carouselRadius, setCarouselRadius] = useState(initialCarouselRadius);

  useEffect(() => {
    if (parabolaValue > 0) {
      setCarouselRadius((spacingValue * spacingValue) / (2 * parabolaValue));
    } else {
      setCarouselRadius(0);
    }
  }, [spacingValue, parabolaValue, width]);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    watchDrag: false,
    // On mobile, align slides to the center so the currently selected card
    // matches the item positioned in the middle of the viewport.
    // Desktop keeps "start" alignment since slide positions are handled
    // manually for the 3D layout.
    align: isMobile ? 'center' : 'start',
    startIndex: initialIndexRef.current ?? 0,
  });

  // When the number of carousel items changes, reinitialize Embla so that it
  // recalculates slide positions. Without this, returning to a previous
  // carousel (e.g. artists after viewing songs) can leave the slides stacked
  // on top of each other.
  const prevItemCount = useRef(itemCount);
  useEffect(() => {
    if (!emblaApi) {
      prevItemCount.current = itemCount;
      return;
    }
    if (prevItemCount.current !== itemCount) {
      const savedIndex =
        typeof initialIndexRef.current === 'number'
          ? initialIndexRef.current
          : selectedIndexRef.current;
      console.log('Reinitializing Embla carousel');
      console.log('Saved index before reInit:', savedIndex);
      emblaApi.reInit();
      emblaApi.scrollTo(savedIndex, true);
      selectedIndexRef.current = savedIndex;
      setSelectedIndex(savedIndex);
      if (onReInit) {
        console.log('onReInit fired with index:', savedIndex);
        onReInit(savedIndex);
      }
      hasInitialized.current = false;
    }
    prevItemCount.current = itemCount;
  }, [emblaApi, itemCount, initialIndexRef, onReInit]);

  const dragStart = useRef<number | null>(null);
  const isDragging = useRef(false);
  const rafRef = useRef<number>();
  const prefersReducedMotion = useReducedMotion();
  const [selectedIndex, setSelectedIndex] = useState(initialIndexRef.current ?? 0);
  const selectedIndexRef = useRef(initialIndexRef.current ?? 0);
  const hasRestoredInitialIndex = useRef(initialIndexRef.current === undefined);
  const animating = useRef<'next' | 'prev' | null>(null);
  const centerTimeout = useRef<ReturnType<typeof setTimeout>>();
  const wheelTimeout = useRef<ReturnType<typeof setTimeout>>();
  const dragThreshold = 1;
  const dragDelta = useRef(0);
  const hasInitialized = useRef(false);

  const pixelDeltaToDistance = (engine: any, delta: number): number =>
    (delta / engine.containerRect.width) * engine.limit.length;

  const clearCenterTimeout = (): void => {
    if (centerTimeout.current) {
      clearTimeout(centerTimeout.current);
      centerTimeout.current = undefined;
    }
  };

  const scheduleCenter = (): void => {
    clearCenterTimeout();
    centerTimeout.current = setTimeout(() => {
      if (!emblaApi) return;
      if (dragStart.current !== null || animating.current !== null) return;
      const target = (selectedIndexRef.current + 0.5) / itemCount;
      const progress = getScrollProgress(emblaApi);
      const diff = target - progress;
      if (Math.abs(diff) > CENTERING_THRESHOLD) {
        const engine = emblaApi.internalEngine();
        const distance = diff * engine.limit.length;
        scrollBy(emblaApi, distance);
      }
    }, 150);
  };

  const handlePointerDown = (evt: React.PointerEvent<HTMLDivElement>): void => {
    if (evt.button !== 0) {
      evt.stopPropagation();
      return;
    }
    clearCenterTimeout();
    dragStart.current = evt.clientX;
    isDragging.current = false;
  };

  const handlePointerMove = (evt: React.PointerEvent<HTMLDivElement>): void => {
    if (dragStart.current === null || !emblaApi) return;
    const delta = -(evt.clientX - dragStart.current) * 0.1;
    if (!isDragging.current) {
      if (Math.abs(delta) > dragThreshold) {
        isDragging.current = true;
        evt.currentTarget.setPointerCapture(evt.pointerId);
        // Reset drag start to avoid a large jump on first drag event
        dragStart.current = evt.clientX;
        return;
      } else {
        return;
      }
    }
    evt.preventDefault();
    clearCenterTimeout();
    const engine = emblaApi.internalEngine();
    const distance = pixelDeltaToDistance(engine, delta);
    scrollBy(emblaApi, distance);
    dragDelta.current = distance;
    dragStart.current = evt.clientX;
  };

  const handlePointerUp = (evt: React.PointerEvent<HTMLDivElement>): void => {
    if (dragStart.current === null || !emblaApi) return;
    dragStart.current = null;
    isDragging.current = false;
    dragDelta.current = 0;
    if (evt.currentTarget.hasPointerCapture(evt.pointerId)) {
      evt.currentTarget.releasePointerCapture(evt.pointerId);
    }
  };

  const handleWheel = (evt: React.WheelEvent<HTMLDivElement>): void => {
    if (!emblaApi) return;
    evt.preventDefault();
    clearCenterTimeout();
    const engine = emblaApi.internalEngine();
    const distance = pixelDeltaToDistance(engine, evt.deltaY) * 0.1;
    scrollBy(emblaApi, distance);
    if (wheelTimeout.current) clearTimeout(wheelTimeout.current);
    wheelTimeout.current = setTimeout(scheduleCenter, 100);
  };

  const scrollPrev = (): void => {
    if (!emblaApi) {
      console.warn('scrollPrev called before Embla is initialized');
      return;
    }
    const engine = emblaApi.internalEngine?.();
    if (!engine) return;
    if (animating.current === 'next') {
      engine.animation.stop();
      animating.current = null;
    }
    animating.current = 'prev';
    clearCenterTimeout();
    scrollBy(emblaApi, -47);
  };

  const scrollNext = (): void => {
    if (!emblaApi) {
      console.warn('scrollNext called before Embla is initialized');
      return;
    }
    const engine = emblaApi.internalEngine?.();
    if (!engine) return;
    if (animating.current === 'prev') {
      engine.animation.stop();
      animating.current = null;
    }
    animating.current = 'next';
    clearCenterTimeout();
    scrollBy(emblaApi, 47);
  };

  useEffect(() => {
    if (!emblaApi) return;
    const onSettle = () => {
      animating.current = null;
      scheduleCenter();
    };
    emblaApi.on('settle', onSettle);
    return () => {
      emblaApi.off('settle', onSettle);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const { slideNodes, internalEngine } = emblaApi;
    const slides = slideNodes();

    if (
      !hasRestoredInitialIndex.current &&
      typeof initialIndexRef.current === 'number'
    ) {
      const index = initialIndexRef.current;
      emblaApi.scrollTo(index, true);
      selectedIndexRef.current = index;
      setSelectedIndex(index);
    }

    const { translate, slideLooper } = internalEngine();
    if (!isMobile) {
      translate.toggleActive(false);
      translate.clear();
      slideLooper.loopPoints.forEach((point) => {
        point.translate.toggleActive(false);
        point.translate.clear();
      });
    } else {
      slides.forEach((slide) => {
        slide.style.transform = '';
        slide.style.opacity = '1';
      });
    }

    const update = () => {
      if (!hasRestoredInitialIndex.current) {
        rafRef.current = requestAnimationFrame(update);
        return;
      }
      const progress = getScrollProgress(emblaApi);

      const engine = internalEngine();
      const velocity = engine.scrollBody.velocity();
      if (
        dragStart.current === null &&
        animating.current === null &&
        Math.abs(velocity) < LOW_SPEED_THRESHOLD &&
        !centerTimeout.current
      ) {
        scheduleCenter();
      }

      const wheelIndex = progress * itemCount - 0.5;
      const maxIndex = itemCount - 1;
      const idx = Math.round(wheelIndex);
      const clamped = clamp(idx, 0, maxIndex);
      selectedIndexRef.current = clamped;
      setSelectedIndex((prev) => (prev === clamped ? prev : clamped));
      if (!isMobile) {
        const threshold = itemCount / 2;
        slides.forEach((slide, index) => {
          let offset = index - wheelIndex;
          let didWrap = false;
          if (offset > threshold) {
            offset -= itemCount;
            didWrap = true;
          } else if (offset < -threshold) {
            offset += itemCount;
            didWrap = true;
          }
          slide.style.transition = prefersReducedMotion
            ? ''
            : didWrap
              ? 'none'
              : 'transform 0.3s ease-in-out';
          const translateX = offset * spacingValue;
          const inner = slide.firstElementChild as HTMLElement | null;
          const translateZ = -parabolaValue * offset * offset;
          slide.style.transform =
            `translate(-50%, -50%) translateX(${translateX}px) translateZ(${translateZ}px)`;
          slide.style.transformStyle = 'preserve-3d';
          slide.style.willChange = 'transform, opacity';
          slide.style.opacity = `${1 - Math.min(Math.abs(offset) / threshold, 1)}`;
          if (inner) {
            inner.style.transform = 'none';
            inner.style.transformStyle = 'preserve-3d';
          }
        });
      }
      rafRef.current = requestAnimationFrame(update);
    };

    rafRef.current = requestAnimationFrame(update);
    if (
      !hasRestoredInitialIndex.current &&
      typeof initialIndexRef.current === 'number'
    ) {
      requestAnimationFrame(() => {
        hasRestoredInitialIndex.current = true;
      });
    } else {
      hasRestoredInitialIndex.current = true;
    }
    if (!hasInitialized.current) {
      onInit?.(emblaApi);
      console.log('Carousel initialization complete', {
        initialIndex: initialIndexRef.current,
        selectedSnap: emblaApi.selectedScrollSnap(),
      });
      hasInitialized.current = true;
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [
    emblaApi,
    itemCount,
    prefersReducedMotion,
    spacingValue,
    parabolaValue,
    isMobile,
    onInit,
  ]);

  useEffect(() => {
    return () => {
      clearCenterTimeout();
      if (wheelTimeout.current) clearTimeout(wheelTimeout.current);
    };
  }, []);

  return {
    emblaRef,
    emblaApi,
    selectedIndex,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleWheel,
    scrollPrev,
    scrollNext,
    isMobile,
    carouselRadius,
  } as const;
};
